<template>
    <div class="d-flex flex-column h-100 overflow-hidden">
        <div class="flex-shrink-0 border-bottom p-2">
            <div class="d-flex w-100 align-items-start">
                <div class="input-group input-group-sm d-flex flex-nowrap w-100">
                    <div class="input-group-prepend">
                        <span class="input-group-text"><i class="fa-solid fa-magnifying-glass"></i></span>
                    </div>
                    <input v-model.trim="searchText" type="text" class="form-control w-100 h-100 py-2 flex-grow-1"
                        placeholder="Search in watchlist (tradingsymbol / exchange / product)"
                        @keydown.enter.prevent="onEnterSearch" />
                    <div class="input-group-append" v-if="searchText">
                        <button class="btn btn-outline-secondary" type="button" @click="clearSearch" title="Clear">
                            <i class="fa-solid fa-xmark"></i>
                        </button>
                    </div>
                </div>
                <div class="ml-2 position-relative">
                    <button type="button" class="btn btn-light btn-sm" title="Sort" @click.stop="toggleSortPanel">
                        <i class="fa-solid fa-sliders"></i>
                    </button>
                    <div v-if="sortOpen" class="dropdown-menu dropdown-menu-right p-3 show" style="min-width: 360px;"
                        @click.stop>
                        <div class="d-flex align-items-center justify-content-between mb-2">
                            <div class="text-uppercase text-muted small d-flex align-items-center">
                                CHANGE TYPE
                                <span class="ml-1" title="Select baseline for change"><i class="fa-solid fa-circle-info"></i></span>
                            </div>
                        </div>
                        <div class="d-flex align-items-center mb-2">
                            <div class="custom-control custom-radio mr-4">
                                <input class="custom-control-input" type="radio" id="ctClose" value="CLOSE"
                                    v-model="changeType" />
                                <label class="custom-control-label" for="ctClose">Close price</label>
                            </div>

                            <div class="custom-control custom-radio">
                                <input class="custom-control-input" type="radio" id="ctOpen" value="OPEN"
                                    v-model="changeType" />
                                <label class="custom-control-label" for="ctOpen">Open price</label>
                            </div>
                        </div>
                        <div class="text-uppercase text-muted small mb-2">SHOW</div>
                        <div class="d-flex align-items-center mb-2">
                            <div class="custom-control custom-checkbox mr-4">
                                <input class="custom-control-input" type="checkbox" id="showChg"
                                    v-model="show.priceChange" @change="onShowChangeToggled" />
                                <label class="custom-control-label" for="showChg">Price change</label>
                            </div>
                            <div class="custom-control custom-checkbox">
                                <input class="custom-control-input" type="checkbox" id="showChgPct"
                                    v-model="show.priceChangePct" @change="onShowChangeToggled" />
                                <label class="custom-control-label" for="showChgPct">Price change %</label>
                            </div>
                        </div>
                        <hr class="my-2" />
                        <div class="text-uppercase text-muted small mb-2">Sort by</div>
                        <div class="d-flex align-items-center mb-2 flex-wrap">
                            <button type="button" class="btn btn-sm mr-2 mb-2"
                                :class="sort.key === 'CHG' ? 'btn-primary' : 'btn-outline-secondary'"
                                @click="setSort('CHG')" :disabled="!show.priceChange" title="Sort by Price change">
                                CHG
                            </button>
                            <button type="button" class="btn btn-sm mr-2 mb-2"
                                :class="sort.key === 'CHGPCT' ? 'btn-primary' : 'btn-outline-secondary'"
                                @click="setSort('CHGPCT')" :disabled="!show.priceChangePct"
                                title="Sort by Price change %">
                                %
                            </button>
                            <button type="button" class="btn btn-sm mr-2 mb-2"
                                :class="sort.key === 'LTP' ? 'btn-primary' : 'btn-outline-secondary'"
                                @click="setSort('LTP')">
                                LTP
                            </button>
                            <button type="button" class="btn btn-sm mr-2 mb-2"
                                :class="sort.key === 'AZ' ? 'btn-primary' : 'btn-outline-secondary'"
                                @click="setSort('AZ')">
                                A-Z
                            </button>
                            <button type="button" class="btn btn-sm mb-2"
                                :class="sort.key === 'EXCH' ? 'btn-primary' : 'btn-outline-secondary'"
                                @click="setSort('EXCH')">
                                EXCH <span class="ml-1">{{ sort.dir === "DESC" ? "↓" : "↑" }}</span>
                            </button>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mt-2">
                            <button type="button" class="btn btn-link p-0" @click="resetSort">Reset</button>
                            <button type="button" class="btn btn-sm btn-primary" @click="closeSortPanel">Save</button>
                        </div>

                        <div class="text-muted small mt-2">Sort and display rules apply to watchlist rows.</div>
                    </div>
                </div>
            </div>
            <div class="mt-2 text-muted small" v-if="searchText">
                Showing matches in watchlist…
            </div>
        </div>
        <div class="flex-shrink-0 border-bottom px-2 py-2">
            <div class="d-flex align-items-center justify-content-between">
                <div class="text-muted text-nowrap text-sm">
                    {{ activeWatchlist.name }} ({{ totalItems(activeWatchlist) }} / 250)
                </div>
            </div>
        </div>
        <div class="flex-grow-1 overflow-auto">
            <div v-for="g in activeWatchlist.groups" :key="g.id" class="border-bottom">
                <div class="d-flex align-items-center justify-content-between px-2 py-2">
                    <div class="d-flex align-items-center text-truncate">
                        <span class="text-muted mr-2"><i class="fa-solid fa-bars"></i></span>
                        <strong class="text-truncate text-xs">{{ g.name }}</strong>
                        <span class="text-muted ml-2 text-xs">({{ g.items.length }})</span>
                    </div>
                    <div class="d-flex align-items-center">
                        <button type="button" class="btn btn-link p-0 mr-3"
                            :title="isGroupCollapsed(g.id) ? 'Expand' : 'Collapse'" @click="toggleGroupCollapse(g.id)">
                            <span v-if="isGroupCollapsed(g.id)">▾</span>
                            <span v-else>▴</span>
                        </button>
                    </div>
                </div>
                <div v-if="!isGroupCollapsed(g.id)">
                    <div v-for="it in pagedGroupItems(g)" :key="it.id" class="border-top px-2 py-2"
                        @mouseenter="hoverItemId = it.id" @mouseleave="hoverItemId = null">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1 text-truncate">
                                <div class="d-flex align-items-center">
                                    <div class="font-weight-bold text-truncate text-xs" :class="symbolColorClass(it)">
                                        {{ it.tradingsymbol }}
                                    </div>
                                </div>
                                <div v-if="it.product" class="text-muted text-uppercase text-xs">
                                    {{ it.product }}
                                </div>
                            </div>
                            <div class="ml-2 d-flex align-items-center justify-content-end">
                                <div v-if="hoverItemId !== it.id && canShowQuotes(it)" class="text-right text-xs">
                                    <div class="d-flex align-items-center justify-content-end">
                                        <span v-if="show.priceChange" class="mr-3" :class="chgClassBy(it)">
                                            {{ format2(getChg(it)) }}
                                        </span>
                                        <span v-if="show.priceChangePct" class="mr-3" :class="chgClassBy(it)">
                                            {{ format2(getChgPct(it)) }}%
                                            <span class="ml-1" v-if="Number(getChg(it)) !== 0">
                                                <span v-if="Number(getChg(it)) > 0">▲</span>
                                                <span v-else>▼</span>
                                            </span>
                                        </span>
                                        <span class="font-weight-bold" :class="chgClassBy(it)">
                                            {{ format2(it.ltp) }}
                                        </span>
                                    </div>
                                </div>
                                <div v-else-if="hoverItemId === it.id" class="btn-group btn-group-sm">
                                    <button type="button" class="btn btn-outline-secondary px-3"
                                        @click="toggleDepth(it.id)" title="Market depth">
                                        <i class="fa-solid fa-bars"></i>
                                    </button>
                                    <button type="button" class="btn btn-outline-secondary px-3" @click="openChart(it)"
                                        title="Chart">
                                        <i class="fa-solid fa-chart-line"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div v-if="openDepthIds.has(it.id)" class="mt-2">
                            <div class="card">
                                <div class="card-body p-2">
                                    <div class="d-flex justify-content-between">
                                        <div class="text-muted small">Open</div>
                                        <div class="text-nowrap">{{ format2(it.open) }}</div>
                                        <div class="text-muted small">Prev. Close</div>
                                        <div class="text-nowrap">{{ format2(it.prevClose) }}</div>
                                    </div>
                                    <div class="d-flex justify-content-between mt-1">
                                        <div class="text-muted small">Low</div>
                                        <div class="text-nowrap">{{ format2(it.low) }}</div>
                                        <div class="text-muted small">High</div>
                                        <div class="text-nowrap">{{ format2(it.high) }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="searchText && groupFilteredCount(g) === 0" class="px-2 py-2 text-muted small">
                        No matches in this group.
                    </div>
                    <div v-else-if="g.items.length === 0" class="px-2 py-2 text-muted small">
                        No items in this group.
                    </div>
                </div>
            </div>
        </div>
        <div class="flex-shrink-0 border-top px-2 py-2">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                    <a v-for="p in visiblePages" :key="'p' + p" href="#" class="mr-3"
                        :class="p === page ? 'font-weight-bold text-primary' : 'text-muted'" @click.prevent="page = p">
                        {{ p }}
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
export default {
    name: "Watchlist",
    emits: ["open-chart"],
    data() {
        return {
            searchText: "",
            activeWatchlistId: "w1",
            page: 1,
            pageSize: 25,
            openDepthIds: new Set(),
            collapsedGroups: new Set(),
            hoverItemId: null,
            sortOpen: false,
            sort: { key: "EXCH", dir: "ASC" },
            changeType: "CLOSE",
            show: {
                priceChange: true,
                priceChangePct: true,
            },
            watchlists: [
                { id: "w1", name: "Watchlist 1", groups: [{ id: "g1", name: "Default", items: [] }] },
                { id: "w2", name: "Watchlist 2", groups: [{ id: "g2", name: "Default", items: [] }] },
            ],
        };
    },
    computed: {
        activeWatchlist() {
            return this.watchlists.find((w) => w.id === this.activeWatchlistId) || this.watchlists[0];
        },
        flatSortedFilteredItems() {
            const q = (this.searchText || "").trim().toUpperCase();
            const out = [];
            const groups = this.activeWatchlist?.groups || [];
            for (const g of groups) {
                let items = q
                    ? (g.items || []).filter((it) => {
                        const sym = (it.tradingsymbol || "").toUpperCase();
                        const prod = (it.product || "").toUpperCase();
                        const ex = (it.exchange || "").toUpperCase();
                        return sym.includes(q) || prod.includes(q) || ex.includes(q);
                    })
                    : (g.items || []).slice();

                items.sort((a, b) => this.compareForSort(a, b));
                items.forEach((it) => out.push({ groupId: g.id, item: it }));
            }
            return out;
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.flatSortedFilteredItems.length / this.pageSize));
        },
        visiblePages() {
            const max = Math.min(7, this.totalPages);
            const pages = [];
            for (let i = 1; i <= max; i++) pages.push(i);
            return pages;
        },
    },
    mounted() {
        document.addEventListener("click", this.onDocClick);
    },
    beforeUnmount() {
        document.removeEventListener("click", this.onDocClick);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.onDocClick);
    },
    created() {
        const g = this.watchlists[0].groups[0];
        const seed = [
            this.makeRandomFuture({ tradingsymbol: "RELIANCE23MAY2025FUT", product: "FUTURE", expiry: "2023-05-25" }),
            { tradingsymbol: "GOLD", product: "COMMODITY_FUTURES", expiry: "2023-05-25" },
            { tradingsymbol: "SILVER", product: "COMMODITY_FUTURES", expiry: "2023-05-25" },
            this.makeRandomFuture({ tradingsymbol: "NIFTY23MAY2025FUT", product: "FUTURE", expiry: "2023-05-25" }),
        ];
        g.items = [];
        seed.forEach((s) => g.items.push(this.makeItemFromApi(s)));
        this.ensureValidPage();
    },
    methods: {
        onDocClick() {
            this.sortOpen = false;
        },
        toggleSortPanel() {
            this.sortOpen = !this.sortOpen;
        },
        closeSortPanel() {
            this.sortOpen = false;
        },
        onShowChangeToggled() {
            if (!this.show.priceChange && !this.show.priceChangePct) {
                this.show.priceChangePct = true;
            }
            if (this.sort.key === "CHG" && !this.show.priceChange) this.sort.key = "LTP";
            if (this.sort.key === "CHGPCT" && !this.show.priceChangePct) this.sort.key = "LTP";
        },
        setSort(key) {
            if (this.sort.key === key) this.sort.dir = this.sort.dir === "ASC" ? "DESC" : "ASC";
            else {
                this.sort.key = key;
                this.sort.dir = "ASC";
            }
            this.page = 1;
            this.ensureValidPage();
        },
        resetSort() {
            this.sort = { key: "EXCH", dir: "ASC" };
            this.changeType = "CLOSE";
            this.show = { priceChange: true, priceChangePct: true };
            this.page = 1;
            this.ensureValidPage();
        },
        onEnterSearch() {
            this.page = 1;
            this.ensureValidPage();
        },
        clearSearch() {
            this.searchText = "";
            this.page = 1;
            this.ensureValidPage();
        },
        ensureValidPage() {
            if (this.page > this.totalPages) this.page = this.totalPages;
            if (this.page < 1) this.page = 1;
        },
        isGroupCollapsed(groupId) {
            return this.collapsedGroups.has(groupId);
        },
        toggleGroupCollapse(groupId) {
            if (this.collapsedGroups.has(groupId)) this.collapsedGroups.delete(groupId);
            else this.collapsedGroups.add(groupId);
            this.collapsedGroups = new Set(this.collapsedGroups);
        },
        openChart(it) {
            this.$emit("open-chart", {
                tradingsymbol: it?.tradingsymbol,
                product: it?.product,
                expiry: it?.expiry,
                exchange: it?.exchange,
            });
        },
        toggleDepth(itemId) {
            if (this.openDepthIds.has(itemId)) this.openDepthIds.delete(itemId);
            else this.openDepthIds.add(itemId);
            this.openDepthIds = new Set(this.openDepthIds);
        },
        getBasePrice(it) {
            return this.changeType === "OPEN" ? it?.open : it?.prevClose;
        },
        getChg(it) {
            const ltp = Number(it?.ltp);
            const base = Number(this.getBasePrice(it));
            if (!Number.isFinite(ltp) || !Number.isFinite(base)) return null;
            return ltp - base;
        },
        getChgPct(it) {
            const chg = Number(this.getChg(it));
            const base = Number(this.getBasePrice(it));
            if (!Number.isFinite(chg) || !Number.isFinite(base) || base === 0) return null;
            return (chg / base) * 100;
        },
        chgClassBy(it) {
            const v = Number(this.getChg(it));
            if (v > 0) return "text-success";
            if (v < 0) return "text-danger";
            return "text-muted";
        },
        canShowQuotes(it) {
            if (!Number.isFinite(Number(it?.ltp))) return false;
            const base = this.getBasePrice(it);
            if (!Number.isFinite(Number(base))) return false;
            return true;
        },
        format2(n) {
            const num = Number(n);
            if (!Number.isFinite(num)) return "-";
            return num.toFixed(2);
        },
        symbolColorClass(it) {
            const v = Number(this.getChg(it));
            if (v > 0) return "text-success";
            if (v < 0) return "text-danger";
            return "text-dark";
        },
        makeRandomFuture(base) {
            const sym = String(base.tradingsymbol || "").toUpperCase();
            const basePrice = sym.includes("NIFTY") ? this.rand(22000, 26000) : this.rand(2200, 3200);
            const prevClose = this.round2(basePrice);
            const open = this.round2(prevClose + this.rand(-40, 40));
            const ltp = this.round2(prevClose + this.rand(-120, 120));
            const high = this.round2(Math.max(open, ltp) + this.rand(0, 60));
            const low = this.round2(Math.min(open, ltp) - this.rand(0, 60));
            return {
                ...base,
                exchange: base.exchange || "NSE",
                prevClose,
                open,
                high,
                low,
                ltp,
            };
        },
        rand(min, max) {
            return min + Math.random() * (max - min);
        },
        round2(n) {
            return Math.round(Number(n) * 100) / 100;
        },
        makeItemFromApi(row) {
            return {
                id: "it_" + Math.random().toString(16).slice(2),
                tradingsymbol: row?.tradingsymbol || "",
                product: row?.product || "",
                expiry: row?.expiry || "",
                exchange: row?.exchange || "",
                ltp: row?.ltp,
                open: row?.open,
                high: row?.high,
                low: row?.low,
                prevClose: row?.prevClose,
            };
        },
        groupFilteredCount(group) {
            const q = (this.searchText || "").trim().toUpperCase();
            if (!q) return (group.items || []).length;
            return (group.items || []).filter((it) => {
                const sym = (it.tradingsymbol || "").toUpperCase();
                const prod = (it.product || "").toUpperCase();
                const ex = (it.exchange || "").toUpperCase();
                return sym.includes(q) || prod.includes(q) || ex.includes(q);
            }).length;
        },
        pagedGroupItems(group) {
            const q = (this.searchText || "").trim().toUpperCase();
            let filtered = q
                ? (group.items || []).filter((it) => {
                    const sym = (it.tradingsymbol || "").toUpperCase();
                    const prod = (it.product || "").toUpperCase();
                    const ex = (it.exchange || "").toUpperCase();
                    return sym.includes(q) || prod.includes(q) || ex.includes(q);
                })
                : (group.items || []).slice();
            filtered.sort((a, b) => this.compareForSort(a, b));
            const all = this.flatSortedFilteredItems;
            const start = (this.page - 1) * this.pageSize;
            const end = start + this.pageSize;
            const slice = all.slice(start, end);
            const ids = slice.filter((x) => x.groupId === group.id).map((x) => x.item.id);
            return filtered.filter((it) => ids.includes(it.id));
        },
        compareForSort(a, b) {
            const key = this.sort.key;
            const mul = this.sort.dir === "DESC" ? -1 : 1;
            const text = (v) => (v ?? "").toString().toUpperCase();
            let cmp = 0;
            if (key === "AZ") {
                cmp = text(a.tradingsymbol).localeCompare(text(b.tradingsymbol));
            } else if (key === "EXCH") {
                cmp = text(a.exchange).localeCompare(text(b.exchange));
                if (cmp === 0) cmp = text(a.product).localeCompare(text(b.product));
                if (cmp === 0) cmp = text(a.tradingsymbol).localeCompare(text(b.tradingsymbol));
            } else if (key === "CHG") {
                cmp = this.compareNullableNumber(this.getChg(a), this.getChg(b));
            } else if (key === "CHGPCT") {
                cmp = this.compareNullableNumber(this.getChgPct(a), this.getChgPct(b));
            } else if (key === "LTP") {
                cmp = this.compareNullableNumber(this.toNum(a.ltp), this.toNum(b.ltp));
            }
            return cmp * mul;
        },
        toNum(v) {
            const n = Number(v);
            return Number.isFinite(n) ? n : null;
        },
        compareNullableNumber(a, b) {
            const A = Number.isFinite(Number(a)) ? Number(a) : null;
            const B = Number.isFinite(Number(b)) ? Number(b) : null;
            if (A == null && B == null) return 0;
            if (A == null) return 1;
            if (B == null) return -1;
            return A - B;
        },
        totalItems(w) {
            return (w?.groups || []).reduce((sum, g) => sum + (g.items?.length || 0), 0);
        },
    },
};
</script>
